/**
 * Native runtime init — only runs on Capacitor (iOS / Android).
 *
 * - Configures the StatusBar to match Enferno's dark theme
 * - Hides the SplashScreen once the React app has booted
 * - Wires the App plugin's appUrlOpen for deep links into enferno.app
 *
 * On the web this entire module is a no-op. The check is per-call so the
 * web build can still import this safely.
 */
import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const initStatusBar = async () => {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#000000' });
  } catch {
    /* status bar plugin not available — non-fatal */
  }
};

export const hideSplash = async () => {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch {
    /* splash screen plugin not available — non-fatal */
  }
};

/**
 * Subscribe to deep-link events from enferno.app. Returns an unsubscribe fn.
 * `onPath(pathWithQuery)` is invoked with the in-app path the router should
 * navigate to (e.g. "/dashboard?from=share").
 */
export const onDeepLink = async (onPath) => {
  if (!isNative()) return () => {};
  try {
    const { App } = await import('@capacitor/app');
    const handle = await App.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        const path = `${url.pathname}${url.search}` || '/';
        onPath(path);
      } catch {
        /* malformed url */
      }
    });
    return () => handle.remove();
  } catch {
    return () => {};
  }
};

export const isNativePlatform = isNative;
